/**
 * Выгрузка отобранного в таблицу.
 *
 * Каталог живёт в телефоне, а решения принимают в офисе: партнёру нужен файл,
 * который откроется в Excel и не потребует ничего устанавливать.
 *
 * Формат намеренно скучный: CSV с точкой с запятой, BOM и запятой в дробях —
 * так его открывает русский Excel, не спрашивая ничего и не превращая
 * «1.35» в дату. Красивый xlsx стоил бы отдельной библиотеки в полмегабайта,
 * которую человек у стенда возит в кармане ради одной кнопки.
 */
import {
  rowPerCt, rowCurrency, STATUS_LABEL,
  type StoneRow, type Status,
} from '@core';

const COLUMNS: Array<[string, (r: StoneRow) => string | number | null]> = [
  ['Номер', (r) => r.no],
  ['Вид', (r) => r.values.get('stone_type')?.text ?? null],
  ['Разновидность', (r) => r.values.get('variety')?.text ?? null],
  ['Вес, ct', (r) => r.values.get('weight_ct')?.num ?? null],
  ['Вес от, ct', (r) => r.values.get('weight_min_ct')?.num ?? null],
  ['Вес до, ct', (r) => r.values.get('weight_max_ct')?.num ?? null],
  ['Количество', (r) => r.values.get('quantity')?.num ?? null],
  ['Форма', (r) => r.values.get('shape')?.text ?? null],
  ['Цвет', (r) => r.values.get('color')?.text ?? null],
  ['Чистота', (r) => r.values.get('clarity')?.text ?? null],
  ['Происхождение', (r) => r.values.get('origin')?.text ?? null],
  ['Обработка', (r) => r.values.get('treatment')?.text ?? null],
  ['Размеры', (r) => r.values.get('dimensions')?.text ?? null],
  ['Лаборатория', (r) => r.values.get('lab')?.text ?? null],
  ['Номер отчёта', (r) => r.values.get('report_no')?.text ?? null],
  ['Отчёт проверен', (r) => (r.certVerified ? 'да' : r.hasCert ? 'нет' : '')],
  ['Валюта', (r) => rowCurrency(r)],
  ['Цена за всё', (r) => r.values.get('asking_price')?.num ?? null],
  ['Цена за карат', (r) => r.values.get('asking_price_per_ct')?.num ?? null],
  ['Цена за карат лотом', (r) => r.values.get('asking_price_per_ct_lot')?.num ?? null],
  ['Цена за карат (итог)', (r) => rowPerCt(r)],
  ['Поставщик', (r) => r.supplierName ?? null],
  ['Стенд / офис', (r) => r.where ?? r.booth ?? null],
  ['Решение', (r) => STATUS_LABEL[r.status as Status] ?? r.status],
  ['Снято', (r) => new Date(r.capturedAt).toLocaleString('ru-RU')],
  ['Заметка', (r) => r.note ?? null],
];

function cell(v: string | number | null): string {
  if (v == null) return '';
  // Числа — с запятой: русский Excel иначе читает «1.35» как текст или дату.
  const t = typeof v === 'number' ? String(v).replace('.', ',') : String(v);
  // Кавычки, переносы и разделитель обязаны быть внутри кавычек, иначе одна
  // заметка со словом «поставщик; торгуется» разъедет всю строку.
  return /[";\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
}

export function rowsToCsv(rows: StoneRow[]): string {
  const head = COLUMNS.map(([name]) => cell(name)).join(';');
  const body = rows.map((r) => COLUMNS.map(([, get]) => cell(get(r))).join(';'));
  return '﻿' + [head, ...body].join('\r\n');
}

/**
 * Отдать файл человеку.
 *
 * На телефоне «скачать» — не то действие: файл нужен не в папке, а в письме
 * или в переписке. Поэтому сначала пробуем системный «Поделиться», и только
 * если его нет — обычную загрузку.
 */
export async function shareTable(rows: StoneRow[], name = 'gemsnote'): Promise<'shared' | 'saved'> {
  const stamp = new Date().toISOString().slice(0, 10);
  const filename = `${name}-${stamp}.csv`;
  const blob = new Blob([rowsToCsv(rows)], { type: 'text/csv;charset=utf-8' });

  const file = new File([blob], filename, { type: 'text/csv' });
  const nav = navigator as Navigator & { canShare?: (d: unknown) => boolean };
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: 'Камни из GemsNote' });
      return 'shared';
    } catch {
      // Отказались от «поделиться» — не ошибка; сохраняем файлом.
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'saved';
}
